<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useTeamLead } from 'dashboard/composables/useTeamLead';
import Button from 'dashboard/components-next/button/Button.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import ProductTour from 'dashboard/components-next/tour/ProductTour.vue';
import { tourSeen } from 'dashboard/components-next/tour/tourStorage';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import { getPrompts } from 'dashboard/api/relayVoiceManager';
import BalanceCard from './BalanceCard.vue';
import PromptSetPanel from './PromptSetPanel.vue';
import IvrMenus from './IvrMenus.vue';
import ActivityLog from './ActivityLog.vue';
import OfficeHours from './OfficeHours.vue';
import CallHandling from './CallHandling.vue';

const { t } = useI18n();
const { accountId } = useAccount();
const { isAdmin } = useTeamLead();
const currentUserId = useMapGetter('getCurrentUserID');

const TABS = [
  'overview',
  'hours',
  'calls',
  'greetings',
  'codes',
  'menus',
  'activity',
];
const tab = ref(0);
const prompts = ref(null);
const promptState = ref('loading');
const tourOpen = ref(false);

const tabs = computed(() =>
  TABS.map(key => ({ key, label: t(`TEAM_LEAD.TABS.${key.toUpperCase()}`) }))
);
const set = name => prompts.value?.sets.find(s => s.prompt_set === name);
const library = computed(() =>
  (prompts.value?.sets || []).flatMap(s =>
    s.slots.flatMap(slot =>
      slot.versions.map(v => ({ ...v, prompt_set: s.prompt_set }))
    )
  )
);

const loadPrompts = async () => {
  try {
    prompts.value = await getPrompts(accountId.value);
    promptState.value = 'ok';
  } catch (error) {
    promptState.value =
      error.response?.status === 503 ? 'not_configured' : 'error';
  }
};

const goTo = async key => {
  tab.value = TABS.indexOf(key);
  await nextTick();
};

const tourSteps = computed(() => [
  {
    target: 'team-lead-tabs',
    title: t('TEAM_LEAD.TOUR.WELCOME.TITLE'),
    body: t('TEAM_LEAD.TOUR.WELCOME.BODY'),
    before: () => goTo('overview'),
  },
  {
    target: 'balance',
    title: t('TEAM_LEAD.TOUR.BALANCE.TITLE'),
    body: t('TEAM_LEAD.TOUR.BALANCE.BODY'),
  },
  {
    target: 'threshold',
    title: t('TEAM_LEAD.TOUR.THRESHOLD.TITLE'),
    body: t('TEAM_LEAD.TOUR.THRESHOLD.BODY'),
  },
  {
    target: 'office-hours',
    title: t('TEAM_LEAD.TOUR.HOURS.TITLE'),
    body: t('TEAM_LEAD.TOUR.HOURS.BODY'),
    before: () => goTo('hours'),
  },
  {
    target: 'after-hours-chat',
    title: t('TEAM_LEAD.TOUR.AFTER_HOURS_CHAT.TITLE'),
    body: t('TEAM_LEAD.TOUR.AFTER_HOURS_CHAT.BODY'),
  },
  {
    target: 'queue',
    title: t('TEAM_LEAD.TOUR.QUEUE.TITLE'),
    body: t('TEAM_LEAD.TOUR.QUEUE.BODY'),
    before: () => goTo('calls'),
  },
  {
    target: 'queue-agents',
    title: t('TEAM_LEAD.TOUR.QUEUE_AGENTS.TITLE'),
    body: t('TEAM_LEAD.TOUR.QUEUE_AGENTS.BODY'),
  },
  {
    target: 'recording',
    title: t('TEAM_LEAD.TOUR.RECORDING.TITLE'),
    body: t('TEAM_LEAD.TOUR.RECORDING.BODY'),
  },
  {
    target: 'blocklist',
    title: t('TEAM_LEAD.TOUR.BLOCKLIST.TITLE'),
    body: t('TEAM_LEAD.TOUR.BLOCKLIST.BODY'),
  },
  {
    target: 'voice-switch',
    title: t('TEAM_LEAD.TOUR.VOICE.TITLE'),
    body: t('TEAM_LEAD.TOUR.VOICE.BODY'),
    before: () => goTo('greetings'),
  },
  {
    target: 'prompt-upload',
    title: t('TEAM_LEAD.TOUR.UPLOAD.TITLE'),
    body: t('TEAM_LEAD.TOUR.UPLOAD.BODY'),
  },
  {
    target: 'prompt-tts',
    title: t('TEAM_LEAD.TOUR.TTS.TITLE'),
    body: t('TEAM_LEAD.TOUR.TTS.BODY'),
  },
  {
    target: 'voice-switch',
    title: t('TEAM_LEAD.TOUR.CODES.TITLE'),
    body: t('TEAM_LEAD.TOUR.CODES.BODY'),
    before: () => goTo('codes'),
  },
  {
    target: 'ivr',
    title: t('TEAM_LEAD.TOUR.MENUS.TITLE'),
    body: t('TEAM_LEAD.TOUR.MENUS.BODY'),
    before: () => goTo('menus'),
  },
  {
    target: 'new-prompt',
    title: t('TEAM_LEAD.TOUR.MENU_PROMPTS.TITLE'),
    body: t('TEAM_LEAD.TOUR.MENU_PROMPTS.BODY'),
  },
  {
    target: 'activity',
    title: t('TEAM_LEAD.TOUR.ACTIVITY.TITLE'),
    body: t('TEAM_LEAD.TOUR.ACTIVITY.BODY'),
    before: () => goTo('activity'),
  },
  {
    target: 'tour-button',
    title: t('TEAM_LEAD.TOUR.AGENTS.TITLE'),
    body: isAdmin.value
      ? t('TEAM_LEAD.TOUR.AGENTS.BODY_ADMIN')
      : t('TEAM_LEAD.TOUR.AGENTS.BODY'),
  },
]);

const tourKey = computed(() => `team-lead-v2-${currentUserId.value}`);

onMounted(() => {
  loadPrompts();
  if (!tourSeen(tourKey.value)) tourOpen.value = true;
});
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        :title="t('TEAM_LEAD.TITLE')"
        :description="t('TEAM_LEAD.DESCRIPTION')"
        feature-name="team_lead"
      >
        <template #actions>
          <Button
            :label="t('PRODUCT_TOUR.TAKE')"
            icon="i-lucide-compass"
            variant="faded"
            color="slate"
            size="sm"
            data-tour="tour-button"
            @click="tourOpen = true"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div class="flex flex-col gap-5 pb-10">
        <div data-tour="team-lead-tabs" class="w-fit">
          <TabBar
            :tabs="tabs"
            :initial-active-tab="tab"
            @tab-changed="selected => (tab = TABS.indexOf(selected.key))"
          />
        </div>

        <template v-if="TABS[tab] === 'overview'">
          <BalanceCard />
        </template>

        <template v-else-if="TABS[tab] === 'activity'">
          <ActivityLog />
        </template>

        <OfficeHours v-else-if="TABS[tab] === 'hours'" />

        <CallHandling v-else-if="TABS[tab] === 'calls'" />

        <template v-else>
          <woot-loading-state
            v-if="promptState === 'loading'"
            :message="t('TEAM_LEAD.LOADING')"
          />
          <p
            v-else-if="promptState !== 'ok'"
            class="rounded-xl bg-n-alpha-1 px-5 py-4 text-sm text-n-slate-11"
          >
            {{
              promptState === 'not_configured'
                ? t('TEAM_LEAD.NOT_CONFIGURED')
                : t('TEAM_LEAD.PROMPTS.UNAVAILABLE')
            }}
          </p>
          <template v-else>
            <PromptSetPanel
              v-if="TABS[tab] === 'greetings'"
              :set="set('inbound')"
              :library="library"
              :tts="prompts.tts"
              @changed="loadPrompts"
            />
            <PromptSetPanel
              v-if="TABS[tab] === 'codes'"
              :set="set('otp')"
              :library="library"
              :tts="prompts.tts"
              @changed="loadPrompts"
            />
            <template v-if="TABS[tab] === 'menus'">
              <IvrMenus :ivr-set="set('ivr')" />
              <PromptSetPanel
                :set="set('ivr')"
                :library="library"
                :tts="prompts.tts"
                @changed="loadPrompts"
              />
            </template>
          </template>
        </template>
      </div>
      <ProductTour
        v-model="tourOpen"
        :steps="tourSteps"
        :storage-key="tourKey"
      />
    </template>
  </SettingsLayout>
</template>
